import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ShoppingCart, ArrowRight, ArrowLeft, CaretLeft, CaretRight, CheckCircle, MagnifyingGlassPlus, Play, ShieldCheck, Star, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getProduct, submitQuote } from "@/lib/api";
import { formatGHS, formatUSD } from "@/lib/format";
import { useCart } from "@/lib/cart";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [showQuote, setShowQuote] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    setActiveIdx(0);
    getProduct(slug)
      .then(setProduct)
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-32 text-zinc-400" data-testid="product-loading">
        Loading product…
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-32" data-testid="product-not-found">
        <h1 className="font-display text-4xl font-black uppercase">Product not found</h1>
        <Link to="/products" className="mt-6 inline-block text-[#FFC107] uppercase tracking-wider font-bold">
          ← Back to catalog
        </Link>
      </div>
    );
  }

  const handleAdd = () => {
    addItem(product, qty);
    toast.success(`${product.name} added to cart`, { duration: 2500 });
  };

  const handleBuyNow = () => {
    addItem(product, qty);
    navigate("/checkout");
  };

  const soldOut = product.stock_qty === 0;

  const media = [
    { type: "image", src: product.image_url },
    ...(product.video_url ? [{ type: "video", src: product.video_url }] : []),
    ...(product.gallery || []).map((src) => ({ type: "image", src })),
  ];
  const current = media[Math.min(activeIdx, media.length - 1)];
  const goPrev = () => setActiveIdx((activeIdx - 1 + media.length) % media.length);
  const goNext = () => setActiveIdx((activeIdx + 1) % media.length);

  return (
    <div data-testid="product-detail-page" className="bg-[#0a0a0a] text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10">
        <Link
          to="/products"
          data-testid="back-to-products"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-[#FFC107]"
        >
          <ArrowLeft size={14} weight="bold" /> Back to catalog
        </Link>
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-7">
          <div data-testid="product-media-carousel" className="relative aspect-[4/3] bg-[#141414] border border-white/10 overflow-hidden">
            {current.type === "video" ? (
              <video
                key={current.src}
                data-testid="product-video"
                src={current.src}
                poster={product.image_url}
                controls
                playsInline
                preload="metadata"
                className="w-full h-full object-contain bg-black"
              />
            ) : (
              <button
                data-testid="open-lightbox"
                onClick={() => setLightboxOpen(true)}
                className="w-full h-full cursor-zoom-in group"
                aria-label="Zoom image"
              >
                <img
                  key={current.src}
                  data-testid="product-main-image"
                  src={current.src}
                  alt={product.name}
                  className="w-full h-full object-contain bg-black"
                />
                <span className="absolute bottom-3 left-3 bg-black/70 border border-white/15 px-2.5 py-1.5 flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-300 group-hover:text-[#FFC107] transition-colors z-10">
                  <MagnifyingGlassPlus size={14} weight="bold" /> Zoom
                </span>
              </button>
            )}
            {product.badge && (
              <span className="absolute top-4 left-4 bg-[#FFC107] text-black text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1.5 z-10">
                {product.badge}
              </span>
            )}
            {media.length > 1 && (
              <>
                <button
                  data-testid="carousel-prev"
                  onClick={goPrev}
                  aria-label="Previous image"
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-[#FFC107] hover:text-black border border-white/20 flex items-center justify-center z-10 transition-colors"
                >
                  <CaretLeft size={18} weight="bold" />
                </button>
                <button
                  data-testid="carousel-next"
                  onClick={goNext}
                  aria-label="Next image"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-[#FFC107] hover:text-black border border-white/20 flex items-center justify-center z-10 transition-colors"
                >
                  <CaretRight size={18} weight="bold" />
                </button>
                <span
                  data-testid="carousel-counter"
                  className="absolute bottom-3 right-3 bg-black/70 border border-white/15 px-2.5 py-1 font-mono-accent text-[11px] tracking-widest z-10"
                >
                  {activeIdx + 1} / {media.length}
                </span>
              </>
            )}
          </div>

          {media.length > 1 && (
            <div className="mt-4 flex gap-3 overflow-x-auto" data-testid="carousel-thumbnails">
              {media.map((m, i) => (
                <button
                  key={`${m.type}-${m.src}`}
                  data-testid={`media-thumb-${i}`}
                  onClick={() => setActiveIdx(i)}
                  className={`relative w-20 h-20 shrink-0 border overflow-hidden bg-black transition-colors ${
                    i === activeIdx ? "border-[#FFC107]" : "border-white/15 hover:border-white/40"
                  }`}
                >
                  <img
                    src={m.type === "video" ? product.image_url : m.src}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  {m.type === "video" && (
                    <span className="absolute inset-0 flex items-center justify-center bg-black/50">
                      <Play size={18} weight="fill" className="text-[#FFC107]" />
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="mt-8 grid grid-cols-2 gap-4">
            <div className="border border-white/10 p-5 bg-[#141414]">
              <ShieldCheck size={26} weight="duotone" className="text-[#FFC107] mb-3" />
              <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Warranty</p>
              <p className="font-display font-bold">12-24 months</p>
            </div>
            <div className="border border-white/10 p-5 bg-[#141414]">
              <CheckCircle size={26} weight="duotone" className="text-[#FFC107] mb-3" />
              <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Stock</p>
              <p className="font-display font-bold" data-testid="stock-indicator">
                {product.stock_qty === 0
                  ? "Out of stock"
                  : product.stock_qty <= 5
                  ? `Only ${product.stock_qty} left`
                  : "Available"}
              </p>
            </div>
          </div>
        </div>

        <div className="md:col-span-5">
          <span className="inline-block font-mono-accent text-[11px] uppercase tracking-[0.2em] border border-white/20 px-2 py-1 text-zinc-300 mb-4">
            {product.category}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight leading-[0.95]">
            {product.name}
          </h1>
          <div className="flex items-center gap-2 mt-4 text-[#FFC107]">
            <Star size={16} weight="fill" />
            <span className="font-mono-accent text-sm">{product.rating?.toFixed(1)}</span>
            <span className="text-zinc-500 text-sm">/ 5.0 (124 reviews)</span>
          </div>

          <p className="mt-6 text-zinc-300 text-lg leading-relaxed">{product.description}</p>

          <div className="mt-8 border-y border-white/10 py-6">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">Price</div>
            <div className="font-display text-4xl font-black text-[#FFC107]">{formatGHS(product.price_ghs)}</div>
          </div>

          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center border border-white/20 h-12">
              <button
                data-testid="qty-decrease"
                onClick={() => setQty(Math.max(1, qty - 1))}
                className="w-12 h-12 hover:text-[#FFC107] font-bold"
              >
                −
              </button>
              <span data-testid="qty-value" className="w-12 text-center font-display font-bold">{qty}</span>
              <button
                data-testid="qty-increase"
                onClick={() => setQty(qty + 1)}
                className="w-12 h-12 hover:text-[#FFC107] font-bold"
              >
                +
              </button>
            </div>
            <button
              data-testid="add-to-cart-btn"
              onClick={handleAdd}
              disabled={soldOut}
              className="flex-1 h-12 px-6 border border-white/30 hover:border-[#FFC107] hover:text-[#FFC107] uppercase tracking-wider text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-white/30 disabled:hover:text-white"
            >
              <ShoppingCart size={18} weight="bold" /> {soldOut ? "Sold out" : "Add to cart"}
            </button>
          </div>

          <button
            data-testid="buy-now-btn"
            onClick={handleBuyNow}
            disabled={soldOut}
            className="mt-3 w-full h-12 px-6 bg-[#FFC107] hover:bg-[#FFB300] text-black uppercase tracking-wider text-sm font-black flex items-center justify-center gap-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {soldOut ? "Out of stock" : "Buy now"} <ArrowRight size={18} weight="bold" />
          </button>

          {product.paystack_page_url && (
            <a
              href={product.paystack_page_url}
              target="_blank"
              rel="noreferrer"
              data-testid="paystack-direct-buy"
              className="mt-3 w-full h-12 px-6 bg-[#0BA4DB] hover:bg-[#0891C0] text-white uppercase tracking-wider text-sm font-black flex items-center justify-center gap-2 transition-colors"
            >
              Pay directly on Paystack <ArrowRight size={18} weight="bold" />
            </a>
          )}

          <button
            data-testid="request-quote-btn"
            onClick={() => setShowQuote(true)}
            className="mt-3 w-full h-11 px-6 bg-transparent text-zinc-400 hover:text-[#FFC107] uppercase tracking-wider text-xs font-bold"
          >
            Need bulk pricing? Request a quote →
          </button>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            {product.features?.map((f, i) => (
              <div key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                <CheckCircle size={16} weight="fill" className="text-[#FFC107] mt-0.5 shrink-0" />
                {f}
              </div>
            ))}
          </div>

          {product.specs && Object.keys(product.specs).length > 0 && (
            <div className="mt-10 border-t border-white/10 pt-6">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FFC107] mb-4">// Specifications</p>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                {Object.entries(product.specs).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-white/5 py-2">
                    <dt className="text-zinc-500 uppercase tracking-wider text-xs">{k}</dt>
                    <dd className="font-mono-accent">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      </div>

      {showQuote && (
        <QuoteModal product={product} onClose={() => setShowQuote(false)} />
      )}

      {lightboxOpen && (
        <Lightbox media={media} startIdx={activeIdx} poster={product.image_url} onClose={() => setLightboxOpen(false)} />
      )}
    </div>
  );
}

const Lightbox = ({ media, startIdx, poster, onClose }) => {
  const [idx, setIdx] = useState(startIdx);
  const [zoomed, setZoomed] = useState(false);
  const [origin, setOrigin] = useState("50% 50%");
  const slide = media[idx];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  const go = (dir) => {
    setZoomed(false);
    setIdx((i) => (i + dir + media.length) % media.length);
  };

  const pointOrigin = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    return `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
  };

  const handleImgClick = (e) => {
    e.stopPropagation();
    if (!zoomed) setOrigin(pointOrigin(e));
    setZoomed(!zoomed);
  };

  return (
    <div
      data-testid="image-lightbox"
      className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      <button
        data-testid="lightbox-close"
        onClick={onClose}
        aria-label="Close zoom"
        className="absolute top-4 right-4 w-11 h-11 bg-black/60 hover:bg-[#FFC107] hover:text-black border border-white/20 flex items-center justify-center z-20 transition-colors"
      >
        <X size={20} weight="bold" />
      </button>
      <span className="absolute top-5 left-5 font-mono-accent text-xs tracking-widest text-zinc-400 z-20">
        {idx + 1} / {media.length}
      </span>

      {media.length > 1 && (
        <>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            aria-label="Previous"
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 bg-black/60 hover:bg-[#FFC107] hover:text-black border border-white/20 flex items-center justify-center z-20 transition-colors"
          >
            <CaretLeft size={20} weight="bold" />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            aria-label="Next"
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 bg-black/60 hover:bg-[#FFC107] hover:text-black border border-white/20 flex items-center justify-center z-20 transition-colors"
          >
            <CaretRight size={20} weight="bold" />
          </button>
        </>
      )}

      <div className="w-full h-full flex items-center justify-center p-4 md:p-12 overflow-hidden" onClick={onClose}>
        {slide.type === "video" ? (
          <video
            src={slide.src}
            poster={poster}
            controls
            playsInline
            autoPlay
            className="max-w-full max-h-full"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <img
            data-testid="lightbox-image"
            src={slide.src}
            alt=""
            onClick={handleImgClick}
            onMouseMove={(e) => zoomed && setOrigin(pointOrigin(e))}
            style={{ transform: zoomed ? "scale(2.5)" : "scale(1)", transformOrigin: origin }}
            className={`max-w-full max-h-full object-contain select-none transition-transform duration-200 ${
              zoomed ? "cursor-zoom-out" : "cursor-zoom-in"
            }`}
            draggable={false}
          />
        )}
      </div>
    </div>
  );
};

const QuoteModal = ({ product, onClose }) => {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    quantity: 10,
    vehicle_type: product.category,
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitQuote({
        ...form,
        product_id: product.id,
        product_name: product.name,
      });
      toast.success("Quote request received. We'll be in touch within 24 hours.");
      onClose();
    } catch {
      toast.error("Could not send quote request. Try WhatsApp instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="quote-modal">
      <div className="bg-[#0a0a0a] border border-white/15 w-full max-w-xl">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h3 className="font-display text-xl font-black uppercase">Request a Quote</h3>
          <button onClick={onClose} data-testid="quote-modal-close" className="hover:text-[#FFC107]">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-sm text-zinc-400">For: <span className="text-white font-bold">{product.name}</span></p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input data-testid="quote-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="h-11 px-3 bg-[#141414] border border-white/15 focus:border-[#FFC107] outline-none text-sm" />
            <input data-testid="quote-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (e.g. 0244 000 000)" className="h-11 px-3 bg-[#141414] border border-white/15 focus:border-[#FFC107] outline-none text-sm" />
          </div>
          <input data-testid="quote-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full h-11 px-3 bg-[#141414] border border-white/15 focus:border-[#FFC107] outline-none text-sm" />
          <input data-testid="quote-qty" required type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value || 1) })} placeholder="Quantity" className="w-full h-11 px-3 bg-[#141414] border border-white/15 focus:border-[#FFC107] outline-none text-sm" />
          <textarea data-testid="quote-message" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Notes (installation, timeline, etc.)" rows={3} className="w-full p-3 bg-[#141414] border border-white/15 focus:border-[#FFC107] outline-none text-sm" />
          <button data-testid="quote-submit" disabled={submitting} type="submit" className="w-full h-12 bg-[#FFC107] hover:bg-[#FFB300] text-black font-black uppercase tracking-wider text-sm disabled:opacity-50">
            {submitting ? "Sending…" : "Send quote request"}
          </button>
        </form>
      </div>
    </div>
  );
};
